import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from '@/lib/toast'

export function useMyFamily() {
  return useQuery({
    queryKey: ['my-family'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return []
      const { data, error } = await supabase
        .from('family_relationships')
        .select('*, to_resident:residents!family_relationships_to_resident_id_fkey(id, first_name, last_name, photo_storage_path)')
        .eq('from_resident_id', session.user.id)
      if (error) throw error
      return data ?? []
    },
  })
}

export function useIncomingFamilyRequests() {
  return useQuery({
    queryKey: ['incoming-family-requests'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return []
      const { data, error } = await supabase
        .from('family_link_requests')
        .select('*, from_resident:residents!family_link_requests_from_resident_id_fkey(id, first_name, last_name, photo_storage_path)')
        .eq('to_resident_id', session.user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
      if (error) throw error
      return data ?? []
    },
  })
}

export function useRequestFamilyLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { username: string; type: string; message?: string }) => {
      const { data, error } = await supabase.rpc('request_family_link', {
        _target_username: input.username,
        _relationship_type: input.type,
        _message: input.message ?? null,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-family'] })
      toast.success('Request sent. The user will be notified.')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useApproveFamilyLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.rpc('approve_family_link_request', { _request_id: requestId })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-family', 'incoming-family-requests'] })
      toast.success('Linked')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useRejectFamilyLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.rpc('reject_family_link_request', { _request_id: requestId })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incoming-family-requests'] })
      toast.success('Declined')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useAddManualFamily() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      relationshipType: string; firstName: string; middleName?: string; lastName: string
      birthDate?: string; mobile?: string; notes?: string; sameBarangay?: boolean
    }) => {
      const { data, error } = await supabase.rpc('add_manual_family_member', {
        _relationship_type: input.relationshipType,
        _first_name: input.firstName,
        _middle_name: input.middleName ?? null,
        _last_name: input.lastName,
        _birth_date: input.birthDate ?? null,
        _mobile: input.mobile ?? null,
        _notes: input.notes ?? null,
        _is_residing_in_same_barangay: input.sameBarangay ?? null,
      })
      if (error) {
        if (error.code === '23505') {
          const type = input.relationshipType
          throw new Error(`You already have a ${type} in your profile.`)
        }
        throw error
      }
      if (data && typeof data === 'object' && 'error' in data) throw new Error((data as { error: string }).error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-family'] })
      toast.success('Family member added')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useRemoveFamily() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('family_relationships').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-family'] })
      toast.success('Removed')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
